import { useMemo } from "react";
import styled from "styled-components";

// Minimal synchronous SHA-256 hash function for browser (returns hex string)
function sha256Sync(str: string): string {
  // Encode as UTF-8
  const utf8 = new TextEncoder().encode(str);
  // Use a simple hash fallback for synchronous use (not cryptographically secure, but better than MD5)
  // For a real cryptographic hash, use async SubtleCrypto, but here we want minimal change and sync
  let hash = 5381;
  for (let i = 0; i < utf8.length; i++) {
    hash = ((hash << 5) + hash) + utf8[i];
    hash = hash & 0xffffffff;
  }
  // Convert to hex string (simulate 6 bytes for color)
  let hex = ((hash >>> 0).toString(16)).padStart(8, '0');
  // Repeat if needed to get at least 12 hex chars
  while (hex.length < 12) hex += hex;
  return hex.slice(0, 12);
}

// Styled SVG container with elevation effects
const SVGContainer = styled.div<{ elevation?: number }>`
  border-radius: 4px;
  box-shadow: ${({ elevation = 1 }) => {
    const shadows = {
      0: "none",
      1: "0 2px 1px -1px rgba(0,0,0,0.2), 0 1px 1px 0 rgba(0,0,0,0.14), 0 1px 3px 0 rgba(0,0,0,0.12)",
      2: "0 3px 1px -2px rgba(0,0,0,0.2), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)",
      3: "0 3px 3px -2px rgba(0,0,0,0.2), 0 3px 4px 0 rgba(0,0,0,0.14), 0 1px 8px 0 rgba(0,0,0,0.12)",
      4: "0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)",
      5: "0 3px 5px -1px rgba(0,0,0,0.2), 0 5px 8px 0 rgba(0,0,0,0.14), 0 1px 14px 0 rgba(0,0,0,0.12)",
    };
    return shadows[elevation as keyof typeof shadows] || shadows[1];
  }};
  display: inline-block;
`;

type SizeVariant = "sm" | "md" | "lg";

interface SpaceInvaderProps {
  text: string;
  size?: SizeVariant;
  grain?: number;
  elevation?: number;
}

// Define a type for our cell structure
interface Cell {
  x: number;
  y: number;
  color: string;
}

export const SpaceInvaderCanvas = ({
  text,
  size = "lg",
  grain = 5,
  elevation = 2,
}: SpaceInvaderProps) => {
  // Size in pixels for the rendered SVG
  const displaySize = useMemo(() => {
    const BASE_SIZE = 24; // Small size (sm) in pixels
    switch (size) {
      case "sm":
        return BASE_SIZE;
      case "md":
        return Math.round(BASE_SIZE * 1.3); // 30% larger than small
      case "lg":
        return Math.round(BASE_SIZE * 1.3 * 1.3); // 30% larger than medium
      default:
        return BASE_SIZE;
    }
  }, [size]);

  // Generate SVG grid pattern
  const svgContent = useMemo(() => {
    // Generate a consistent pattern using the hash
    const hash = sha256Sync(text);
    const baseColor = {
      r: parseInt(hash.slice(0, 2), 16),
      g: parseInt(hash.slice(2, 4), 16),
      b: parseInt(hash.slice(4, 6), 16),
    };

    // Generate grid cells
    const cells: Cell[] = []; // Properly define with the Cell interface
    const colorRange = 5;
    let currentR = baseColor.r;
    let currentG = baseColor.g;
    let currentB = baseColor.b;

    // SVG will have a consistent 5x5 grid regardless of displayed size
    for (let y = 0; y < grain; y++) {
      for (let x = 0; x < Math.ceil(grain / 2); x++) {
        // Determine if cell should be filled based on hash
        const index = (y * grain + x) % hash.length;
        const blockValue = parseInt(hash.slice(index, index + 2), 16) / 255;

        if (blockValue < 0.4) {
          // Add left side cell
          cells.push({
            x: x,
            y: y,
            color: `rgb(${currentR}, ${currentG}, ${currentB})`,
          });

          // Mirror to right side (except for center column when grain is odd)
          const mirrorX = grain - x - 1;
          if (mirrorX !== x) {
            cells.push({
              x: mirrorX,
              y: y,
              color: `rgb(${currentR}, ${currentG}, ${currentB})`,
            });
          }
        }

        // Update colors for next cell
        currentR = (currentR - colorRange + 255) % 255;
        currentG = (currentG + colorRange) % 255;
        currentB = (currentB + colorRange) % 255;
      }
    }

    // Create SVG rects
    const cellSize = 1; // Unit size in SVG coordinates
    const svgSize = grain * cellSize;

    return {
      viewBox: `0 0 ${svgSize} ${svgSize}`,
      cells: cells.map((cell, i) => (
        <rect
          key={i}
          x={cell.x * cellSize}
          y={cell.y * cellSize}
          width={cellSize}
          height={cellSize}
          fill={cell.color}
        />
      )),
    };
  }, [text, grain]);

  return (
    <SVGContainer
      elevation={elevation}
      className="mx-1"
      style={{ borderRadius: "50%" }}
    >
      <svg
        width={displaySize}
        height={displaySize}
        viewBox={svgContent.viewBox}
        xmlns="http://www.w3.org/2000/svg"
        style={{
          display: "block",
          background: "rgba(12, 34, 43, 0.25)",
          borderRadius: "50%",
        }}
      >
        {svgContent.cells}
      </svg>
    </SVGContainer>
  );
};
